/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { addToast } from "actions/ToastMessageActions";
import { useDispatch } from "react-redux";
import {
  CompletedEnhanceSpeechTrack,
  findEndOfCurrentRange,
  timeRangesToString,
} from "components/EnhancedSpeech/Utilities";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";
import { useExpiringUrl } from "hooks/useExpiringUrl";
import { computeTwoSliderGains } from "components/EnhancedSpeech/computeTwoSliderGains";

export interface EnhanceStreamingAudioPlayerHook {
  play: () => void;
  pause: () => void;
  seekTo: (seekTimeSeconds: number) => void;
  isPlaying: boolean;
  bufferedRegionPercent: number;
  currentTime: number;
  readyToPlay: boolean;
  isAwaitingData: boolean;
  isStalled: boolean;
  videoElementRef: MutableRefObject<HTMLVideoElement | null>;
}

const useEnhancedStreamingAudioPlayer = (
  enhanceStrengthPercent: number,
  backgroundPercent: number,
  enhancementEnabled: boolean,
  track: CompletedEnhanceSpeechTrack,
  videoContainerElementRef: MutableRefObject<HTMLDivElement | null>
): EnhanceStreamingAudioPlayerHook => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const mediaElementRef = useRef<HTMLMediaElement | null>(null);
  const audioSourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  const videoSourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  const enhancedGainNodeRef = useRef<GainNode | null>(null);
  const isolatedSpeechGainNodeRef = useRef<GainNode | null>(null);
  const originalGainNodeRef = useRef<GainNode | null>(null);
  const backgroundGainNodeRef = useRef<GainNode | null>(null);
  const channelSplitterNodeRef = useRef<ChannelSplitterNode | null>(null);
  const channelMergerNodeRef = useRef<ChannelMergerNode | null>(null);
  const unmounted = useRef(false);
  const trackRef = useRef(track);
  trackRef.current = track;
  const dispatch = useDispatch();
  const isEnhanceSpeechV2Ref = useRef(false);
  isEnhanceSpeechV2Ref.current = track.modelVersion === ModelVersion.v2;

  const expiringTrackUrl = useExpiringUrl({
    refreshUrl: track.mergedMediaUrl,
  });

  const [isPlaying, setIsPlaying] = useState(false);
  const isPlayingRef = useRef(false);
  isPlayingRef.current = isPlaying;
  const [currentTime, setCurrentTime] = useState(0);
  const [bufferedRanges, setBufferedRanges] = useState<TimeRanges | null>(null);
  const [bufferedRegionPercent, setBufferedRegionPercent] = useState(0);
  const [playerInitialized, setPlayerInitialized] = useState(false);
  const [isAwaitingData, setIsAwaitingData] = useState(false);
  const [isStalled, setIsStalled] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const wasPlayingBeforeSeekingRef = useRef(false);

  const endBuffering = (): void => {
    setIsAwaitingData(false);
    setIsStalled(false);
  };

  const play = useCallback((): void => {
    audioContextRef.current?.resume();
    mediaElementRef.current?.play();
    setIsPlaying(true);
  }, [setIsPlaying]);

  const pause = useCallback((): void => {
    audioContextRef.current?.suspend();
    mediaElementRef.current?.pause();
    setIsPlaying(false);
  }, [setIsPlaying]);

  const seekTo = useCallback(
    (seekTimeSeconds: number, preservePlayingState: boolean = true): void => {
      if (!mediaElementRef.current) {
        return;
      }

      wasPlayingBeforeSeekingRef.current =
        preservePlayingState && isPlayingRef.current;

      pause();
      mediaElementRef.current.currentTime = seekTimeSeconds;
      setCurrentTime(seekTimeSeconds);
    },
    [pause, setCurrentTime]
  );

  const resetPlayer = useCallback((): void => {
    pause();
    endBuffering();
    seekTo(0, false);
    setIsSeeking(false);
  }, [pause, seekTo]);

  const updateGain = useCallback(
    (
      enhanceSpeechPercent: number,
      backgroundPercent: number,
      enhancementEnabled: boolean
    ): void => {
      if (
        !(
          audioContextRef.current &&
          enhancedGainNodeRef.current &&
          isolatedSpeechGainNodeRef.current &&
          originalGainNodeRef.current &&
          backgroundGainNodeRef.current
        )
      ) {
        return;
      }

      const enhanceGains = computeTwoSliderGains(
        enhanceSpeechPercent,
        backgroundPercent,
        enhancementEnabled,
        isEnhanceSpeechV2Ref.current ? ModelVersion.v2 : ModelVersion.v1
      );

      enhancedGainNodeRef.current.gain.setValueAtTime(
        enhanceGains.enhancedSpeechGain,
        audioContextRef.current.currentTime
      );

      isolatedSpeechGainNodeRef.current.gain.setValueAtTime(
        enhanceGains.isolatedSpeechGain,
        audioContextRef.current.currentTime
      );

      originalGainNodeRef.current.gain.setValueAtTime(
        enhanceGains.originalGain,
        audioContextRef.current.currentTime
      );

      backgroundGainNodeRef.current.gain.setValueAtTime(
        enhanceGains.backgroundGain,
        audioContextRef.current.currentTime
      );
    },
    []
  );

  // Reset player when track changes
  useEffect(() => {
    resetPlayer();
  }, [resetPlayer, track]);

  // Initialize Context, nodes, and elements
  useEffect(() => {
    /*
      Create and hook up audio context and audio nodes:
      ┌─────────────────────────────────────────────┐
      │MediaElementAudioSourceNode(HTMLMediaElement)│
      │                      │                      │
      │                      ▼                      │
      │             ChannelSplitterNode             │
      │                      │                      │
      │               ┌──────┴──────┐               │
      │               │             │               │
      │               ▼             ▼               │
      │           GainNode      GainNode            │
      │          (original)    (enhanced)           │
      │               │             │               │
      │               └──────┬──────┘               │
      │                      │                      │
      │                      ▼                      │
      │              ChannelMergerNode              │
      │                      │                      │
      │                      ▼                      │
      │             AudioDestinationNode            │
      └─────────────────────────────────────────────┘
    */

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }

    const audioContext = audioContextRef.current;

    if (!channelMergerNodeRef.current) {
      channelMergerNodeRef.current = audioContext.createChannelMerger(1);
      channelMergerNodeRef.current.channelCount = 1;
      channelMergerNodeRef.current.channelCountMode = "explicit";
      channelMergerNodeRef.current.channelInterpretation = "speakers";

      channelMergerNodeRef.current.connect(audioContext.destination);
    }

    const channelMergerNode = channelMergerNodeRef.current;

    if (!originalGainNodeRef.current) {
      originalGainNodeRef.current = audioContext.createGain();
      originalGainNodeRef.current.connect(channelMergerNode, 0, 0);
    }

    const originalGainNode = originalGainNodeRef.current;

    if (!enhancedGainNodeRef.current) {
      enhancedGainNodeRef.current = audioContext.createGain();
      enhancedGainNodeRef.current?.connect(channelMergerNode, 0, 0);
    }

    const enhancedGainNode = enhancedGainNodeRef.current;

    if (!backgroundGainNodeRef.current) {
      backgroundGainNodeRef.current = audioContext.createGain();
      backgroundGainNodeRef.current?.connect(channelMergerNode, 0, 0);
    }

    const backgroundGainNode = backgroundGainNodeRef.current;

    if (!isolatedSpeechGainNodeRef.current) {
      isolatedSpeechGainNodeRef.current = audioContext.createGain();
      isolatedSpeechGainNodeRef.current?.connect(channelMergerNode, 0, 0);
    }

    const isolatedSpeechGainNode = isolatedSpeechGainNodeRef.current;

    if (!channelSplitterNodeRef.current) {
      channelSplitterNodeRef.current = audioContext.createChannelSplitter(4);
      channelSplitterNodeRef.current.channelInterpretation = "discrete";
      channelSplitterNodeRef.current.channelCountMode = "explicit";
      channelSplitterNodeRef.current.connect(originalGainNode, 0);
      channelSplitterNodeRef.current.connect(enhancedGainNode, 1);
      channelSplitterNodeRef.current.connect(backgroundGainNode, 2);
      channelSplitterNodeRef.current.connect(isolatedSpeechGainNode, 3);
    }

    if (!videoElementRef.current) {
      videoElementRef.current = document.createElement("video");
      videoElementRef.current.setAttribute("playsinline", "true");
    }

    if (!audioElementRef.current) {
      audioElementRef.current = document.createElement("audio");
    }

    // Don't hook up the MediaElementAudioSourceNodes until we know if the current track is video or not

    if (!audioSourceNodeRef.current) {
      audioSourceNodeRef.current = audioContext.createMediaElementSource(
        audioElementRef.current
      );
    }

    if (!videoSourceNodeRef.current) {
      videoSourceNodeRef.current = audioContext.createMediaElementSource(
        videoElementRef.current
      );
    }
  }, []);

  // Load new media when expiring url changes
  // these refs are here so this media loading doesn't trigger whenever they change :(
  const backgroundPercentRef = useRef<number>(backgroundPercent);
  const enhanceStrengthPercentRef = useRef<number>(enhanceStrengthPercent);
  const enhancementEnabledRef = useRef<boolean>(enhancementEnabled);
  useEffect(() => {
    if (
      !audioContextRef.current ||
      !audioElementRef.current ||
      !videoElementRef.current ||
      !audioSourceNodeRef.current ||
      !videoSourceNodeRef.current ||
      !channelSplitterNodeRef.current ||
      !channelMergerNodeRef.current ||
      !originalGainNodeRef.current ||
      !enhancedGainNodeRef.current ||
      !backgroundGainNodeRef.current ||
      !trackRef.current
    ) {
      console.error(
        "🔥🔥🔥 ERROR: Trying to load media before elements and audio graph are initialized!"
      );

      return;
    }

    setPlayerInitialized(false);
    setIsSeeking(false);

    if (!expiringTrackUrl) {
      return;
    }

    // Switch between audio and video depending on track type
    const mediaElement = trackRef.current?.isVideo
      ? videoElementRef.current
      : audioElementRef.current;
    mediaElementRef.current = mediaElement;

    if (!videoContainerElementRef.current?.hasChildNodes()) {
      videoContainerElementRef.current?.append(videoElementRef.current);
    }
    // TODO: FIGURE OUT HOW TO UNATTACH DEBUG EVENT LISTENERS
    // addEventDebugging(mediaElement); // 🐞 Uncomment to debug media element event stream

    const mediaElementSourceNode = trackRef.current?.isVideo
      ? videoSourceNodeRef.current
      : audioSourceNodeRef.current;

    audioSourceNodeRef.current?.disconnect();
    videoSourceNodeRef.current?.disconnect();

    mediaElementSourceNode?.connect(channelSplitterNodeRef.current);

    // Pause playback and save the current position in case the expiring url got refreshed
    const wasPlaying = isPlayingRef.current;
    if (wasPlaying) {
      pause();
    }
    const playbackTimeAtRefresh = mediaElement.currentTime ?? 0;

    // Reinitialize media element
    mediaElement.crossOrigin = "anonymous";
    mediaElement.preload = "auto";
    mediaElement.src = expiringTrackUrl;
    mediaElement.load();

    const callIfMounted = (callback: () => void): (() => void) => {
      return (): void => {
        if (unmounted.current) {
          return;
        }
        callback();
      };
    };

    mediaElement.onended = callIfMounted(resetPlayer);

    mediaElement.onpause = callIfMounted((): void => endBuffering());

    mediaElement.onplaying = callIfMounted((): void => endBuffering());

    mediaElement.onstalled = callIfMounted((): void => setIsStalled(true));

    mediaElement.onseeking = callIfMounted((): void => setIsSeeking(true));

    mediaElement.onwaiting = callIfMounted((): void => setIsAwaitingData(true));

    mediaElement.ontimeupdate = callIfMounted((): void => {
      endBuffering();
      setCurrentTime(mediaElement.currentTime);
    });

    mediaElement.onprogress = callIfMounted((): void => {
      endBuffering();
      setBufferedRanges(mediaElement.buffered);
    });

    mediaElement.onseeked = callIfMounted((): void => {
      if (wasPlayingBeforeSeekingRef.current) {
        play();
        wasPlayingBeforeSeekingRef.current = false;
      }
      setIsSeeking(false);
    });

    mediaElement.onerror = callIfMounted((): void => {
      resetPlayer();

      const mediaError = mediaElement.error;
      const errorMessage = `Received audio element error. Code: ${mediaError?.code}. Message: ${mediaError?.message}`;

      newrelic.noticeError(new Error(errorMessage), {
        expiringTrackUrl: expiringTrackUrl,
        trackId: trackRef.current.id,
        mergedTrackUrl: trackRef.current.mergedMediaUrl,
      });

      console.error(errorMessage);

      dispatch(
        addToast({ message: I18n.t("enhance_speech.loading_track_failed") })
      );
    });

    updateGain(
      enhanceStrengthPercentRef.current,
      backgroundPercentRef.current,
      enhancementEnabledRef.current
    );
    seekTo(playbackTimeAtRefresh);

    if (wasPlaying) {
      play();
    }

    // 🐞 UNCOMMENT TO COMPARE OUR CUSTOM PLAYER WITH THE NATIVE HTML5 PLAYER FOR DEBUGGING
    // mediaElement.controls = true;
    // if (!trackRef.current.isVideo) {
    //   document
    //     .querySelector("[data-testid='player-container']")
    //     ?.append(mediaElement);
    // }

    setPlayerInitialized(true);
  }, [
    expiringTrackUrl,
    dispatch,
    seekTo,
    pause,
    play,
    resetPlayer,
    updateGain,
    videoContainerElementRef,
  ]);

  // Clean up audio context and nodes to free memory on unmount
  useEffect(() => {
    return (): void => {
      unmounted.current = true;
      audioSourceNodeRef.current?.disconnect();
      videoSourceNodeRef.current?.disconnect();
      channelSplitterNodeRef.current?.disconnect();
      channelMergerNodeRef.current?.disconnect();
      enhancedGainNodeRef.current?.disconnect();
      originalGainNodeRef.current?.disconnect();
      backgroundGainNodeRef.current?.disconnect();
      audioContextRef.current?.close();
    };
  }, []);

  useEffect(() => {
    if (bufferedRanges?.length) {
      const duration = mediaElementRef.current?.duration ?? Infinity;
      const endOfCurrentBufferedRange = findEndOfCurrentRange(
        currentTime,
        bufferedRanges
      );

      setBufferedRegionPercent(
        Math.round((endOfCurrentBufferedRange / duration) * 100.0)
      );
    }
  }, [bufferedRanges, currentTime, track.duration]);

  useEffect(() => {
    updateGain(enhanceStrengthPercent, backgroundPercent, enhancementEnabled);
    enhanceStrengthPercentRef.current = enhanceStrengthPercent;
    backgroundPercentRef.current = backgroundPercent;
    enhancementEnabledRef.current = enhancementEnabled;
  }, [
    enhanceStrengthPercent,
    backgroundPercent,
    enhancementEnabled,
    updateGain,
  ]);

  const readyToPlay = !!expiringTrackUrl && playerInitialized && !isSeeking;

  return {
    play,
    pause,
    seekTo,
    isPlaying,
    bufferedRegionPercent,
    currentTime,
    readyToPlay,
    isAwaitingData,
    isStalled,
    videoElementRef,
  };
};

// 🐞 FOR DEBUGGING MEDIA ELEMENT EVENT STREAM
const addEventDebugging = (mediaElement: HTMLMediaElement): void => {
  const events = [
    "abort",
    "canplay",
    "canplaythrough",
    "durationchange",
    "emptied",
    "ended",
    "error",
    "loadeddate",
    "loadedmetadata",
    "loadstart",
    "pause",
    "play",
    "playing",
    "ratechange",
    "seeked",
    "seeking",
    "stalled",
    "suspend",
    "volumechange",
    "waiting",
    // "timeupdate", // Handled separately
    // "progress", // Handled separately
  ];

  events.forEach((event) => {
    mediaElement.addEventListener(event, () => {
      console.log(`⏯️ =====> HTMLMediaElement event: ${event}`);
    });
  });

  mediaElement.addEventListener("timeupdate", () => {
    console.log(
      `⏰ =====> HTMLMediaElement event: timeupdate [${mediaElement.currentTime}]`
    );
  });

  mediaElement.addEventListener("progress", () => {
    console.log(
      `⏯️ =====> HTMLMediaElement event: progress - ${timeRangesToString(
        mediaElement.buffered
      )}`
    );
  });
};

export default useEnhancedStreamingAudioPlayer;
